import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Gamepad2, ExternalLink } from 'lucide-react';
import PosterPlaceholder from './PosterPlaceholder';
import SiteHeader from './SiteHeader';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * GameDetail — the public page for one game.
 *
 * A game carries no TMDb id, so every game on the calendar linked to
 * /insider/project/:id, which is behind ProtectedRoute: a signed-out visitor's
 * first click on a game was a login wall. This is the page those links open.
 *
 * It is not the film/TV detail page with different words. A game does not have
 * one release date — it arrives per platform, and often in stages (demo, beta,
 * early access, launch) — so the platform rows are the centre of the page, not
 * a footnote under a single date.
 */

const EVENT_LABEL = {
  GAME_ANNOUNCED: 'Announced',
  GAME_REVEALED: 'Revealed',
  GAME_ENTERED_DEVELOPMENT: 'In development',
  GAME_LAUNCHED: 'Launched',
  GAME_CANCELLED: 'Cancelled',
  GAMEPLAY_SHOWCASE: 'Gameplay showcase',
  PLATFORM_RELEASE: 'Platform release',
  EARLY_ACCESS_RELEASED: 'Early access',
  DEMO_RELEASED: 'Demo',
  BETA_STARTED: 'Beta',
  DLC_RELEASED: 'DLC',
  EXPANSION_RELEASED: 'Expansion',
  PATCH_RELEASED: 'Patch',
  SEASON_UPDATE: 'Season update',
  TRAILER_RELEASED: 'Trailer',
  AWARD_NOMINATION: 'Award nomination',
  AWARD_WIN: 'Award win',
};
const eventLabel = (t) => EVENT_LABEL[t]
  || (t || '').toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// The descriptive fields migration 038 keeps whole rather than modelling. Shown
// in this order because it reads from what the game is towards how it plays.
const ATTRIBUTE_ROWS = [
  ['genres', 'Genres'],
  ['themes', 'Themes'],
  ['game_modes', 'Modes'],
  ['player_perspectives', 'Perspective'],
  ['engines', 'Engine'],
];

// Parsed as a local date: new Date('2026-11-20') is UTC midnight, which is the
// 19th anywhere west of Greenwich — a release date is exactly the thing that
// must not be off by a day.
const dateLabel = (d) => {
  if (!d) return null;
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day)
    .toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

// A relation reads differently from each side: a DLC's page names its base
// game, a base game's page lists its DLC.
const RELATION_LABEL = {
  child: { dlc: 'DLC', expansion: 'Expansions', remake: 'Remakes', edition: 'Editions',
           port: 'Ports', bundle: 'Bundles' },
  parent: { dlc: 'DLC for', expansion: 'Expansion of', remake: 'Remake of',
            edition: 'Edition of', port: 'Port of', bundle: 'Bundled in' },
};
const relationLabel = (direction, relation) =>
  (RELATION_LABEL[direction] || {})[relation]
  || (direction === 'parent' ? 'Part of' : 'Related');

export default function GameDetail() {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // No Authorization header: the endpoint is public, and sending a token
      // this page never needs would only make it look gated.
      const resp = await fetch(`${API_BASE}/api/insider/game/${id}`);
      if (!resp.ok) { setNotFound(true); return; }
      setGame(await resp.json());
    } catch {
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-5xl mx-auto px-4 py-16 text-center text-gray-400">
          <Clock className="w-6 h-6 mx-auto mb-2 animate-pulse" /> Loading…
        </div>
      </div>
    );
  }
  if (notFound || !game) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-5xl mx-auto px-4 py-16 text-center">
          <p className="text-gray-500">Game not found.</p>
          <Link to="/calendar" className="text-sm text-videbits-600 hover:underline mt-2 inline-block">
            Back to the calendar
          </Link>
        </div>
      </div>
    );
  }

  const attrs = game.attributes || {};
  const grouped = (game.related || []).reduce((acc, r) => {
    const key = relationLabel(r.direction, r.relation);
    (acc[key] = acc[key] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 pt-4">
        <Link to="/calendar" className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Back to the calendar
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row gap-6 mb-10">
          <div className="w-full sm:w-56 aspect-video sm:aspect-auto sm:h-32 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
            {game.poster_url
              ? <img src={game.poster_url} alt={game.title} className="w-full h-full object-cover" />
              : <PosterPlaceholder />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Gamepad2 className="w-4 h-4 text-videbits-500" />
              <span className="text-xs uppercase tracking-wide text-gray-400">Game</span>
              {game.status && <span className="text-xs text-gray-400">· {game.status}</span>}
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">{game.title}</h1>
            {game.overview && <p className="text-sm text-gray-600 leading-relaxed">{game.overview}</p>}
            {attrs.igdb_slug && (
              <a href={`https://www.igdb.com/games/${attrs.igdb_slug}`}
                 target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-sm font-medium text-videbits-600 hover:text-videbits-800 mt-3">
                View on IGDB <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>

        {/* Platforms first: a game does not have one release date, and "on PS5
            next week" is the thing a player came to find out. */}
        {(game.platforms || []).length > 0 && (
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Platforms</h2>
            <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
              {game.platforms.map((p) => (
                <div key={`${p.id}:${p.release_date}:${p.region || ''}`}
                     className="flex items-center justify-between px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">
                    {p.name}
                    {p.region && <span className="text-xs text-gray-400 ml-2">{p.region}</span>}
                  </div>
                  <div className="text-sm text-gray-600">
                    {dateLabel(p.release_date) || <span className="text-gray-400">No date announced</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {ATTRIBUTE_ROWS.some(([key]) => (attrs[key] || []).length > 0) && (
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">About</h2>
            <dl className="space-y-3">
              {ATTRIBUTE_ROWS.filter(([key]) => (attrs[key] || []).length > 0).map(([key, label]) => (
                <div key={key} className="flex flex-wrap items-baseline gap-2">
                  <dt className="text-xs uppercase tracking-wide text-gray-400 w-24">{label}</dt>
                  <dd className="flex flex-wrap gap-1.5">
                    {attrs[key].map((v) => (
                      <span key={v} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">{v}</span>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {(game.companies || []).length > 0 && (
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Studios</h2>
            <div className="flex flex-wrap gap-2">
              {game.companies.map((c) => (
                <Link key={c.id} to={`/company/${c.id}`}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-videbits-300 hover:text-videbits-700">
                  {c.name}
                  {c.role && <span className="text-xs text-gray-400 ml-1.5">{c.role}</span>}
                </Link>
              ))}
            </div>
          </section>
        )}

        {(game.franchises || []).length > 0 && (
          <section className="mb-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Series</h2>
            <div className="flex flex-wrap gap-2">
              {game.franchises.map((f) => (
                <Link key={f.id} to={`/franchise/${f.id}`}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-videbits-300 hover:text-videbits-700">
                  {f.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        {Object.keys(grouped).length > 0 && (
          <section className="mb-10">
            {Object.entries(grouped).map(([label, items]) => (
              <div key={label} className="mb-6 last:mb-0">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{label}</h2>
                <div className="flex flex-wrap gap-3">
                  {items.map((r) => (
                    <Link key={r.id} to={`/game/${r.id}`} className="w-40 group">
                      <div className="w-40 aspect-video rounded-lg overflow-hidden bg-gray-100 mb-1.5">
                        {r.poster_url
                          ? <img src={r.poster_url} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                          : <PosterPlaceholder />}
                      </div>
                      <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight">{r.title}</p>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {(game.timeline || []).length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
            <div className="space-y-2">
              {game.timeline.map((e) => (
                <div key={e.id} className="flex items-baseline gap-3 text-sm">
                  <span className="w-36 flex-shrink-0 text-gray-400">
                    {dateLabel(e.event_date) || 'Undated'}
                  </span>
                  <span className="font-medium text-gray-900">{eventLabel(e.event_type)}</span>
                  {e.description && <span className="text-gray-500 truncate">{e.description}</span>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
